import { Component, ViewChild } from '@angular/core';
import { AddMultipleComponent, IAddedItem } from 'components/input/add-multiple/add-multiple.component';
import { ButtonComponent } from 'components/input/button/button.component';
import { CustomInputComponent } from 'components/input/custom-input/custom-input.component';
import { CustomSelectComponent } from 'components/input/custom-select/custom-select.component';
import { QueryInputComponent } from 'components/query/query-input.component';
import { QueryType } from 'enums/QueryType';
import { PoolModel } from 'models/generated/Entities.GormCrm.Pool';
import { PoolTeamModel } from 'models/generated/Entities.GormCrm.PoolTeam';
import { QueryModel } from 'models/generated/Entities.GormCrm.Query';
import { DynamicQueryModel } from 'models/generated/Models.DynamicQuery';
import { Comparison, ExpressionFilterModel } from 'models/generated/Models.ExpressionFilter';
import { QueryPropertyModel } from 'models/generated/Models.QueryProperty';
import { SelectListItemModel } from 'models/SelectList';
import { Layout } from 'modules/layout';
import { TranslatePipe } from 'pipes/translate.pipe';
import { Subscription } from 'rxjs';
import { ComponentUrl, NavigationService, UrlParam } from 'services/navigation.service';
import { PoolService } from 'services/pool.service';
import { PushService } from 'services/push.service';
import { QueryService } from 'services/query.service';
import { RelationService } from 'services/relation.service';
import { UserService } from 'services/user.service';

@Component({
	selector: 'view-pool',
	templateUrl: './view-pool.component.html',
	imports: [Layout, CustomInputComponent, CustomSelectComponent, AddMultipleComponent, QueryInputComponent, TranslatePipe]
})
@ComponentUrl('view-pool')
export class ViewPoolComponent {

	@ViewChild('saveButton') saveButton!: ButtonComponent;

	public pool = new PoolModel;
	public addedTeams: IAddedItem[] = [];
	private allQueries: QueryModel[] = [];
	private queriesLoaded = false;
	private selectedQuery?: QueryModel;
	public queriesList: SelectListItemModel[] = [];
	public queryProperties: QueryPropertyModel[] = [];
	public allTeams: IAddedItem[] = [];
	public onPoolChanges: Subscription;
	public operatorType = Comparison;
	
	private _queryData = new DynamicQueryModel();
	set queryData(data: DynamicQueryModel) {
		this._queryData = data;
	}
	get queryData() { return this._queryData; }

	@UrlParam
	set poolId(id: string) {
		if (id) {
			this.getPool(id);
		} else {
			this.loadPoolModel(new PoolModel());
		}
	}
	get poolId() {
		return this.pool && this.pool.id ? this.pool.id : '';
	}

	constructor(private readonly poolService: PoolService
		, private readonly userService: UserService
		, private readonly pushService: PushService
		, private readonly queryService: QueryService
		, private readonly navigationService: NavigationService) {
		this.getQueries();

		this.onPoolChanges = this.pushService.onPoolChanges.subscribe(event => {
			if (this.pool.id === event.data.poolId) {
				this.getPool(event.data.poolId);
			}
		});
	}

	loadPoolModel(pool: PoolModel) {
		this.pool = pool;
		this.allTeams = this.userService.activeTeams.map((team) => { return { value: team.id!, label: team.name }; });

		const addedTeams: IAddedItem[] = [];
		this.pool.poolTeams.forEach(pt => {
			var team = this.userService.team(pt.teamId!);
			if (team) addedTeams.push({ value: team.id!, label: team.name } as IAddedItem)
		})
		this.addedTeams = addedTeams;

		this.navigationService.refreshUrl();
	}

	getPool(id: string) {
		this.poolService.getPoolById(id).then(pool => {
			this.loadPoolModel(pool);
			this.loadPoolQuery();
		});
	}

	getQueries() {
		this.queryService.getQueries().then(result => {
			this.queriesList = [];
			this.allQueries = result;
			this.queriesLoaded = true;
			this.loadPoolQuery();
			result.forEach(query => {
				var queryData = new DynamicQueryModel();
				if (query.data != undefined && query.data != '') {
					queryData = RelationService.instantiateModelsFromData(new DynamicQueryModel(), Object.assign(queryData, JSON.parse(query.data)));
					if (queryData!.queryType == QueryType.TaskAction) {
						this.queriesList.push(new SelectListItemModel(query.id!, query.name));
					}
				}
			})
		});
	}

	loadPoolQuery() {
		if (this.queriesLoaded && this.pool.queryId) {
			this.selectedQuery = this.allQueries.find(q => q.id == this.pool.queryId);
			if (this.selectedQuery != undefined && this.selectedQuery.data != '') {
				this.queryData = RelationService.instantiateModelsFromData(new DynamicQueryModel(), JSON.parse(this.selectedQuery.data));
				this.queryProperties = this.queryData.filters.map(filter => filter.property!);
				if (this.pool.data != undefined && this.pool.data != '') {
					this.queryData = RelationService.instantiateModelsFromData(new DynamicQueryModel(), Object.assign(this.queryData, JSON.parse(this.pool.data)));
				}
			}
		}
	}

	selectQuery(queryId: string) {
		this.pool.queryId = queryId;
		this.loadPoolQuery();
	}

	isNullOperator(comparison: Comparison): boolean {
		return (comparison == Comparison.Null || comparison == Comparison.NotNull);
	}

	getOptions(filter: ExpressionFilterModel): { [key: string]: string } {
		if (!this.queryProperties) return {};
		var queryProperty = this.queryProperties.find(queryProperty => queryProperty.name == filter.property!.name);
		if (!queryProperty) return {};
		return queryProperty.options;
	}

	updateQuery() {
		this.queryData.queryType = QueryType.TaskAction;
		this.queryData.include(qd => qd.filters);
		this.queryData.filters.forEach(filter => filter.include(f => f.property));
	}

	savePool() {
		this.pool.poolTeams = [];
		this.addedTeams.forEach(tp => {
			var teamModel = new PoolTeamModel();
			teamModel.teamId = tp.value;
			this.pool.poolTeams.push(teamModel);
		});

		this.pool.include(p => p.poolTeams);
		this.updateQuery();
		this.poolService.savePool(this.pool, this.queryData).then((savedPool) => {
			if (!this.pool.id) {
				this.loadPoolModel(savedPool);
				this.loadPoolQuery();
			}
			this.saveButton.succeeded();
		})
	}
}

